import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { ChevronDown } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

// Scroll animation hook
const useScrollAnimation = () => {
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.fade-up');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return ref;
};

// Card Component
const EngagementCard = ({ 
  label, 
  title, 
  content, 
  listItems, 
  ctaText, 
  ctaHref, 
  isOutlined = false,
  closingLine,
  chips,
  delay = 0 
}) => (
  <div 
    className="lhsc-card fade-up"
    style={{ transitionDelay: `${delay}s` }}
  >
    <span className="lhsc-label block">{label}</span>
    <h3 className="font-cormorant text-2xl text-[#2C2C2C] mb-4">{title}</h3>
    <div className="font-lato-light text-base leading-[1.8] text-[#2C2C2C] mb-6 space-y-4">
      {content.map((paragraph, idx) => (
        <p key={idx}>{paragraph}</p>
      ))}
    </div>
    {listItems && (
      <ul className="lhsc-bullet-list mb-6">
        {listItems.map((item, idx) => (
          <li key={idx}>{item}</li>
        ))}
      </ul>
    )}
    {chips && (
      <div className="flex flex-wrap gap-2 mb-6">
        {chips.map((chip, idx) => (
          <span key={idx} className={chip.variant === 'periwinkle' ? 'lhsc-chip-periwinkle lhsc-chip' : 'lhsc-chip'}>
            {chip.text}
          </span>
        ))}
      </div>
    )}
    {closingLine && (
      <p className="font-lato text-sm italic text-[#7E8184] mb-6">{closingLine}</p>
    )}
    <a
      href={ctaHref}
      className={isOutlined ? 'lhsc-btn-outline' : 'lhsc-btn-primary'}
      data-testid={`cta-${title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {ctaText}
    </a>
  </div>
);

// Featured Campaign Card
const FeaturedCampaignCard = () => (
  <div 
    className="bg-[#ABBDDF] rounded-lg overflow-hidden fade-up"
    data-testid="featured-campaign-card"
  >
    <div className="flex flex-col lg:flex-row">
      {/* Text Content */}
      <div className="lg:w-[55%] p-10 lg:p-16">
        <span className="font-josefin text-xs uppercase tracking-[0.15em] text-white block mb-4">
          GLOBAL HUMANITARIAN CAMPAIGN
        </span>
        <h3 className="font-cormorant text-3xl lg:text-[2.5rem] text-white mb-2">
          Closing the Gap
        </h3>
        <span className="font-josefin text-xs uppercase tracking-[0.15em] text-white/90 block mb-6">
          GLOBAL EMPOWERMENT THROUGH WOMEN-LED ACTION
        </span>
        <div className="font-lato-light text-base leading-[1.85] text-white space-y-4 mb-8">
          <p>
            A global women-led campaign operating in 25+ cities worldwide. The Closing the Gap campaign uses an innovative same-day redistribution model that eliminates warehouses, waiting periods, and bureaucracy — allowing for immediate community action every single month from March through November.
          </p>
          <p>
            Local city squads partner directly with community businesses to collect and immediately redistribute surplus essentials — food, hygiene products, clothing, baby and family essentials — to women, families, and organizations in need. Crucially, women are not just beneficiaries. They become active participants, fostering agency and self-reliance at every level of the campaign.
          </p>
          <p>
            This movement is proudly women-led, community-funded, and dignity-first in every interaction.
          </p>
        </div>
        
        {/* Campaign Stats */}
        <div className="flex flex-wrap gap-4 mb-8">
          <div className="bg-white rounded px-4 py-3 text-center">
            <span className="font-cormorant text-2xl text-[#9D6E6D] block">25+</span>
            <span className="font-josefin text-xs uppercase text-[#7E8184]">Cities Worldwide</span>
          </div>
          <div className="bg-white rounded px-4 py-3 text-center">
            <span className="font-cormorant text-2xl text-[#9D6E6D] block">March–November</span>
            <span className="font-josefin text-xs uppercase text-[#7E8184]">Annual Campaign Season</span>
          </div>
          <div className="bg-white rounded px-4 py-3 text-center">
            <span className="font-cormorant text-2xl text-[#9D6E6D] block">Same-Day</span>
            <span className="font-josefin text-xs uppercase text-[#7E8184]">Redistribution Model</span>
          </div>
        </div>
        
        <a
          href="https://entrepreneurladyhustler.com/en/closing-the-gap-womens-humanitarian-campaign/"
          className="inline-block bg-white text-[#9D6E6D] font-josefin text-sm uppercase tracking-[0.1em] px-8 py-4 rounded hover:bg-[#F2E1E1] transition-colors duration-200"
          data-testid="cta-explore-campaign"
        >
          Explore the Campaign →
        </a>
      </div>
      
      {/* Decorative Right Panel */}
      <div className="hidden lg:block lg:w-[45%] bg-[#9D6E6D]">
        <div className="h-full flex items-center justify-center p-12">
          <div className="text-center">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full bg-white/20 flex items-center justify-center">
              <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
            <p className="font-cormorant text-2xl text-white/90 italic">
              "Together, we close the gap."
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// FAQ Section
const FAQSection = () => {
  const faqData = [
    {
      question: "How can I get involved with The Lady Hustler Society Club if I am not an entrepreneur?",
      answer: "LHSC welcomes women at every stage and in every role. You do not need to be an entrepreneur to become a Lady Hustler. Professionals, advocates, students, and community members all find their place within the association. If you have skills to contribute, a story to share, or a community to connect — there is a pathway here for you. Start by exploring membership or reaching out directly to our team."
    },
    {
      question: "What is the Closing the Gap campaign and how can I participate?",
      answer: "Closing the Gap is LHSC's global women-led humanitarian campaign operating in 25+ cities worldwide from March through November each year. It uses a same-day redistribution model to deliver essential goods directly to women, families, and organizations in need — without warehouses, delays, or bureaucracy. You can participate as a city squad volunteer, redistribution network partner, campus organizer, or campaign amplifier."
    },
    {
      question: "Can universities and academic institutions partner with LHSC?",
      answer: "Yes. LHSC builds flexible, impact-driven partnerships with universities, incubators, accelerators, and entrepreneurship hubs. Institutional partnerships can include student and alumni memberships at group rates, co-hosted workshops and masterclasses, campus-based Closing the Gap activations, and career center integration. Every partnership is designed collaboratively to fit your institution's context."
    },
    {
      question: "How does LHSC work with investors and funders interested in women-led businesses?",
      answer: "LHSC prepares women-led businesses before connecting them to investors and funders. Every founder who reaches an investor through LHSC has completed a readiness process. Access is curated to ensure alignment, preparation, and ethical capital deployment. Investors submit an intake form to begin the conversation and are matched with founders based on sector, stage, and values alignment."
    },
    {
      question: "Is The Lady Hustler Society Club a registered nonprofit organization?",
      answer: "Yes. The Lady Hustler Society Club is a federally registered Canadian nonprofit association based in Quebec, operating internationally. LHSC advances women's economic empowerment through education, entrepreneurship support, and community-led humanitarian action across 25+ cities worldwide."
    }
  ];

  return (
    <section className="py-20 bg-[#F8F9FA]" data-testid="faq-section">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 fade-up">
          <span className="lhsc-section-label block">FREQUENTLY ASKED QUESTIONS</span>
          <h2 className="font-cormorant text-3xl lg:text-[2.2rem] text-[#2C2C2C]">
            Everything You Need to Know About Getting Involved
          </h2>
        </div>
        
        <Accordion type="single" collapsible className="fade-up">
          {faqData.map((faq, index) => (
            <AccordionItem 
              key={index} 
              value={`item-${index}`}
              className="border-b border-[#DBC8CE]"
            >
              <AccordionTrigger 
                className="font-josefin text-base text-[#2C2C2C] text-left py-5 hover:no-underline"
                data-testid={`faq-question-${index}`}
              >
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="font-lato-light text-base leading-[1.8] text-[#2C2C2C] pb-5">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

// Main Get Involved Page Component
const GetInvolvedPage = () => {
  useScrollAnimation();

  return (
    <>
      <Helmet>
        <title>Get Involved With LHSC | The Lady Hustler Society Club</title>
        <meta name="description" content="Discover every way to engage with The Lady Hustler Society Club — join as a member, speaker, investor, campus partner, campaign volunteer, or community organization." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://get-involved.entrepreneurladyhustler.com/" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Get Involved | The Lady Hustler Society Club" />
        <meta property="og:description" content="Discover every way to engage with The Lady Hustler Society Club — join as a member, speaker, investor, campus partner, campaign volunteer, or community organization." />
        <meta property="og:url" content="https://get-involved.entrepreneurladyhustler.com/" />
        <meta property="og:image" content="https://get-involved.entrepreneurladyhustler.com/og-image-get-involved.jpg" />
        <meta property="og:site_name" content="The Lady Hustler Society Club" />
        <meta property="og:locale" content="en_CA" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Get Involved | The Lady Hustler Society Club" />
        <meta name="twitter:description" content="Discover every way to engage with The Lady Hustler Society Club — join as a member, speaker, investor, campus partner, campaign volunteer, or community organization." />
        <meta name="twitter:image" content="https://get-involved.entrepreneurladyhustler.com/og-image-get-involved.jpg" />
        
        {/* Hreflang */}
        <link rel="alternate" hreflang="en-ca" href="https://get-involved.entrepreneurladyhustler.com/" />
        <link rel="alternate" hreflang="x-default" href="https://get-involved.entrepreneurladyhustler.com/" />
        
        {/* Schema Markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "Get Involved | The Lady Hustler Society Club",
            "description": "Discover every way to engage with The Lady Hustler Society Club — as a member, speaker, campaign partner, investor, institution, or community organization.",
            "url": "https://get-involved.entrepreneurladyhustler.com/",
            "inLanguage": "en-CA",
            "publisher": {
              "@type": "NGO",
              "name": "The Lady Hustler Society Club",
              "url": "https://entrepreneurladyhustler.com/en/"
            }
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://entrepreneurladyhustler.com/en/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Get Involved",
                "item": "https://get-involved.entrepreneurladyhustler.com/"
              }
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How can I get involved with The Lady Hustler Society Club?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "There are multiple pathways to engage with LHSC — as an individual member, speaker, campaign volunteer, campus partner, community organization, investor, or corporate partner. Each pathway has a dedicated intake process designed to match your skills, resources, and goals with the right initiative."
                }
              },
              {
                "@type": "Question",
                "name": "What is the Closing the Gap campaign and how can I join?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Closing the Gap is LHSC's global women-led humanitarian campaign operating in 25+ cities worldwide. It uses a same-day redistribution model to deliver essential goods directly to women and families in need. You can join as a city squad volunteer, redistribution partner, or campaign amplifier."
                }
              },
              {
                "@type": "Question",
                "name": "Can universities and incubators partner with LHSC?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes. LHSC partners with universities, incubators, accelerators, and entrepreneurship hubs through student and alumni memberships, co-hosted workshops, campus-based Closing the Gap activations, and incubator collaboration. Partnerships are flexible and designed to fit each institution's context."
                }
              },
              {
                "@type": "Question",
                "name": "How does LHSC work with investors and funders?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "LHSC prepares women-led businesses before connecting them to investors and funders. Access is curated to ensure readiness, alignment, and ethical capital deployment. Investors engage with de-risked, prepared founders through LHSC's structured intake and readiness process."
                }
              },
              {
                "@type": "Question",
                "name": "Is The Lady Hustler Society Club a registered nonprofit?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes. The Lady Hustler Society Club is a federally registered Canadian nonprofit association based in Quebec, operating internationally to advance women's economic empowerment through education, entrepreneurship, and community-led humanitarian action."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* HERO SECTION */}
      <section 
        className="bg-[#F2E1E1] pt-24 pb-20"
        data-testid="hero-section"
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="font-josefin text-sm uppercase tracking-[0.15em] text-[#9D6E6D] block mb-4 fade-up">
            WAYS TO GET INVOLVED
          </span>
          
          <h1 className="font-cormorant text-4xl sm:text-5xl lg:text-[3.5rem] text-[#2C2C2C] leading-tight mb-8 fade-up max-w-[780px] mx-auto">
            Multiple Pathways to Engage With The Lady Hustler Society Club
          </h1>
          
          <p className="font-lato-light text-lg leading-[1.85] text-[#2C2C2C] max-w-[680px] mx-auto fade-up">
            The Lady Hustler Society Club is a federally registered Canadian nonprofit based in Quebec, operating internationally to advance women's economic empowerment through education, entrepreneurship, and community-led humanitarian action. We bring together female entrepreneurs, professionals, institutions, companies, nonprofits, and global partners who believe that economic equity requires both opportunity and action. Whether you are looking to contribute your skills, activate a campaign, support women-led businesses, collaborate institutionally, or build community-level impact — there is a clear and intentional way to engage.
          </p>
          
          <div className="mt-12 mx-auto w-[120px] h-px bg-[#DBC8CE] fade-up"></div>
        </div>
      </section>

      {/* SECTION 1: FOR INDIVIDUALS */}
      <section className="py-20 bg-[#F8F9FA]" data-testid="individuals-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 fade-up">
            <span className="lhsc-section-label block">FOR INDIVIDUALS</span>
            <h2 className="font-cormorant text-3xl lg:text-[2.2rem] text-[#2C2C2C] mb-4">
              Personal Pathways for Women, Professionals & Advocates
            </h2>
            <p className="font-lato-light text-base text-[#2C2C2C] max-w-[620px] mx-auto">
              Personal pathways for women, professionals, speakers, and anyone who wants to show up for this movement with intention.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Card 1: Become a Lady Hustler */}
            <EngagementCard
              label="MEMBERSHIP"
              title="Become a Lady Hustler"
              content={[
                "Join a global association of women building businesses, careers, and economic independence. LHSC membership is the starting point for everything — education, incubator pathways, global networking, investor access, and participation in women-led economic and humanitarian initiatives that span 25+ cities worldwide.",
                "As a Lady Hustler, you gain access to the Success Path framework — a four-phase learning journey designed for women at every stage of business. You join a community of 1,900+ women who show up for each other, refer each other, and hold each other accountable. You gain a seat at the table in a global movement that is doing the work, not just talking about it."
              ]}
              listItems={[
                "Education, masterclasses, and workshops",
                "Incubator pathways and micro-grant access",
                "Global networking across 25+ cities",
                "Investor introduction after readiness preparation",
                "Participation in Closing the Gap humanitarian campaign",
                "911 Business Emergency peer support",
                "The Société platform at member pricing"
              ]}
              ctaText="Become a Lady Hustler →"
              ctaHref="https://join.entrepreneurladyhustler.com"
              delay={0.1}
            />

            {/* Card 2: Lend Your Voice */}
            <EngagementCard
              label="SPEAKERS & THOUGHT LEADERS"
              title="Lend Your Voice"
              content={[
                "The Lady Hustler Society Club invites women with expertise, lived experience, and authentic stories to contribute as speakers, facilitators, panelists, and community thought partners. We seek powerful voices for impactful formats — keynote addresses, dynamic panel discussions, skill-building workshops, intimate fireside chats, and inclusive community roundtables.",
                "We value authentic storytelling and lived experience above credentials and titles. Your insights will reach diverse audiences: aspiring entrepreneurs, students, corporate partners, community leaders, and fellow Lady Hustlers across multiple cities and platforms."
              ]}
              listItems={[
                "Women's entrepreneurship and economic independence",
                "Economic justice and closing systemic gaps",
                "Leadership development and breaking barriers in business",
                "Financial literacy and wealth building",
                "Community organizing and grassroots movement building",
                "Humanitarian action and women-led impact"
              ]}
              ctaText="Apply to Speak →"
              ctaHref="https://entrepreneurladyhustler.com/en/for-speakers-panelists-and-thought-leaders/"
              delay={0.2}
            />
          </div>
        </div>
      </section>

      {/* SECTION 2: FOR CAMPAIGN PARTICIPANTS */}
      <section className="py-20 bg-[#F8F9FA]" data-testid="campaign-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 fade-up">
            <span className="lhsc-section-label block">FOR CAMPAIGN PARTICIPANTS</span>
            <h2 className="font-cormorant text-3xl lg:text-[2.2rem] text-[#2C2C2C] mb-4">
              Join the Movement on the Ground
            </h2>
            <p className="font-lato-light text-base text-[#2C2C2C] max-w-[620px] mx-auto">
              Activate the Closing the Gap campaign in your city as a volunteer, redistribution partner, or campus organizer.
            </p>
          </div>

          {/* Featured Campaign Card */}
          <div className="mb-8">
            <FeaturedCampaignCard />
          </div>

          {/* Two Column Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Card 3: Join the Redistribution Network */}
            <EngagementCard
              label="REDISTRIBUTION NETWORK"
              title="Join the Redistribution Network"
              content={[
                "A dignity-first collaborative initiative designed specifically for nonprofits, shelters, transitional housing, food security providers, and grassroots and mutual aid organizations. As part of the global Closing the Gap campaign, the Redistribution Network coordinates same-day delivery of essential goods directly to those who need them — bypassing warehouses, delays, and competition for resources.",
                "Operating annually from March through November, the network features monthly city activations across 25+ cities and culminates in the Global Gala & Solidarity Walk. Local organizations lead all ground delivery. LHSC supports coordination, visibility, training, and shared standards."
              ]}
              listItems={[
                "Food and nutritional essentials",
                "Hygiene and personal care products",
                "Clothing and seasonal essentials",
                "Baby and family necessities",
                "Basic household and emergency supplies"
              ]}
              chips={[
                { text: "DIGNITY-FIRST" },
                { text: "CONSENT-BASED" },
                { text: "RAPID RESPONSE" },
                { text: "LONG-TERM RELATIONSHIPS" }
              ]}
              ctaText="Join the Redistribution Network →"
              ctaHref="https://entrepreneurladyhustler.com/en/join-the-lhsc-redistribution-network/"
              delay={0.1}
            />

            {/* Card 4: Campus Activations */}
            <EngagementCard
              label="CAMPUS & ACADEMIC"
              title="Campus Activations"
              content={[
                "Co-create educational activations with universities, colleges, and academic institutions that connect students and alumni to women-led entrepreneurship, professional development, and real-world global impact. Campus-based Closing the Gap activations bring the movement directly to the next generation of women building careers and businesses.",
                "LHSC campus activations are designed to meet students where they are — combining education, action, and community in formats that work within academic calendars and institutional structures."
              ]}
              listItems={[
                "Student and alumni Lady Hustler memberships (group rates available)",
                "Co-hosted entrepreneurship workshops and masterclasses",
                "Campus-based Closing the Gap redistribution activations",
                "Career center and entrepreneurship hub integration",
                "Guest lectures, panels, and keynotes",
                "Student incubator collaboration and referrals"
              ]}
              ctaText="Request a Campus Activation →"
              ctaHref="https://entrepreneurladyhustler.com/en/campus-activation-request/"
              delay={0.2}
            />
          </div>
        </div>
      </section>

      {/* SECTION 3: FOR INSTITUTIONS & ORGANIZATIONS */}
      <section className="py-20 bg-[#F2E1E1]" data-testid="institutions-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 fade-up">
            <span className="lhsc-section-label block">FOR INSTITUTIONS & ORGANIZATIONS</span>
            <h2 className="font-cormorant text-3xl lg:text-[2.2rem] text-[#2C2C2C] mb-4">
              Structured Partnerships Built on Shared Mission
            </h2>
            <p className="font-lato-light text-base text-[#2C2C2C] max-w-[620px] mx-auto">
              For universities, companies, nonprofits, investors, and mission-aligned institutions ready to build something that lasts.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Card 5: Universities & Incubators */}
            <EngagementCard
              label="ACADEMIC PARTNERSHIPS"
              title="Connect Your Community"
              content={[
                "LHSC partners with universities, incubators, accelerators, and entrepreneurship hubs to expand access, opportunity, and real-world pathways for women. We connect students, alumni, and portfolio companies to education, global networks, and women-led economic initiatives — while helping institutions activate their communities beyond theory.",
                "Our partnerships are flexible, practical, and impact-driven. Whether you want to co-host programming, offer student memberships, support local activations, or integrate into a global women-led network, we design a model that fits your context."
              ]}
              listItems={[
                "Student and alumni memberships (group rates available)",
                "Co-hosted workshops and masterclasses",
                "Campus-based Closing the Gap activations",
                "Incubator and accelerator collaboration or referrals",
                "Career center and entrepreneurship hub integration"
              ]}
              closingLine="This is not a one-size-fits-all partnership. It is an invitation to build pathways together."
              ctaText="Start a Partnership Inquiry →"
              ctaHref="https://entrepreneurladyhustler.com/en/campus-activation-request/"
              isOutlined={true}
              delay={0.1}
            />

            {/* Card 6: Community & Nonprofit Partnerships */}
            <EngagementCard
              label="COMMUNITY PARTNERSHIPS"
              title="Community & Nonprofit Partnerships"
              content={[
                "Our community partnerships are rooted in dignity and consent. This program is designed specifically for community-led organizations, nonprofits, online communities, collectives, grassroots groups, and aligned networks who share our philosophy of collaboration over competition — building waves rather than silos.",
                "This is not a funding application or a competitive intake process. It is an invitation to explore how communities can move side by side. All partnerships are shaped collaboratively and evolve over time to best serve our collective goals."
              ]}
              listItems={[
                "Co-hosted workshops, panels, and conversations",
                "Shared community programming and events",
                "Cross-community learning and dialogue",
                "Joint participation in Closing the Gap activations",
                "Ethical storytelling and community amplification",
                "Resource sharing and referral pathways",
                "Community-led initiatives supported by LHSC infrastructure"
              ]}
              ctaText="Become a Community Partner →"
              ctaHref="https://entrepreneurladyhustler.com/en/community-partner/"
              isOutlined={true}
              delay={0.2}
            />

            {/* Card 7: Strategic & Institutional Partnerships */}
            <EngagementCard
              label="STRATEGIC PARTNERSHIPS"
              title="Strategic & Institutional Partnerships"
              content={[
                "Mission-aligned partnerships, sponsorships, and long-term collaborations with LHSC across programs, campaigns, and global activations. For companies, foundations, media organizations, and institutions whose values align with women's economic empowerment and humanitarian action — and who are ready to move beyond statements into structural support.",
                "LHSC's strategic partnerships are built on shared accountability. Every collaboration is designed to create measurable impact for the women and communities at the center of our mission."
              ]}
              listItems={[
                "Program and campaign sponsorships",
                "Long-term institutional collaborations",
                "Global activation co-partnerships",
                "Media and visibility partnerships",
                "Cause-aligned brand partnerships",
                "Foundation and philanthropic engagement"
              ]}
              ctaText="Start a Partnership Inquiry →"
              ctaHref="https://entrepreneurladyhustler.com/en/institutional-partnership-inquiry/"
              isOutlined={true}
              delay={0.3}
            />
          </div>
        </div>
      </section>

      {/* SECTION 4: FOR BUSINESSES & PROFESSIONALS */}
      <section className="py-20 bg-[#F8F9FA]" data-testid="business-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 fade-up">
            <span className="lhsc-section-label block">FOR BUSINESSES & PROFESSIONALS</span>
            <h2 className="font-cormorant text-3xl lg:text-[2.2rem] text-[#2C2C2C] mb-4">
              Engage With the LHSC Ecosystem as a Business or Professional
            </h2>
            <p className="font-lato-light text-base text-[#2C2C2C] max-w-[620px] mx-auto">
              For companies, agencies, and investors ready to support women's economic empowerment through hiring, funding, and strategic engagement.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Card 8: Investor & Funder Engagement */}
            <EngagementCard
              label="INVESTORS & FUNDERS"
              title="Investor & Funder Engagement"
              content={[
                "Engage with women-led businesses after preparation and de-risking. Access is curated to ensure readiness, alignment, and ethical capital deployment. LHSC prepares founders before connecting them to investors — every introduction is meaningful, intentional, and mutual.",
                "We do not believe in cold introductions. Every founder who reaches an investor through LHSC has been through a readiness process that protects the time and integrity of both parties. This is what ethical capital deployment looks like in practice."
              ]}
              listItems={[
                "Access to prepared, de-risked women-led businesses",
                "Curated introductions based on alignment and readiness",
                "Founder profiles with traction, market validation, and pitch materials",
                "Annual pitch event with pre-qualified participants",
                "Ongoing relationship with a vetted women's economic ecosystem"
              ]}
              ctaText="Investor Intake →"
              ctaHref="https://entrepreneurladyhustler.com/en/investor-funder-intake/"
              delay={0.1}
            />

            {/* Card 9: Hire Women-Led Talent */}
            <EngagementCard
              label="TALENT & HIRING"
              title="Hire Women-Led Talent"
              content={[
                "Access vetted women-led professionals across creative, digital, strategic, and operational fields — while directly supporting a nonprofit economic ecosystem. Every hire through LHSC supports women's economic empowerment in a measurable, concrete way.",
                "LHSC connects companies and agencies with skilled, professional women who have been part of our community and have demonstrated their expertise, reliability, and values alignment. This is not a job board. It is a talent ecosystem built on trust."
              ]}
              chips={[
                { text: "CREATIVE", variant: "periwinkle" },
                { text: "DIGITAL", variant: "periwinkle" },
                { text: "STRATEGIC", variant: "periwinkle" },
                { text: "OPERATIONAL", variant: "periwinkle" }
              ]}
              listItems={[
                "Brand strategy and creative direction",
                "Digital marketing and content creation",
                "Web design and development",
                "Operations and project management",
                "Business strategy and consulting",
                "Community management and communications"
              ]}
              ctaText="Find Women-Led Talent →"
              ctaHref="https://entrepreneurladyhustler.com/en/for-companies-agencies-seeking-talents/"
              delay={0.2}
            />
          </div>
        </div>
      </section>

      {/* SECTION 5: FAQ */}
      <FAQSection />

      {/* SECTION 6: NOT SURE WHERE YOU FIT */}
      <section className="py-24 bg-[#2C2C2C]" data-testid="contact-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="font-josefin text-sm uppercase tracking-[0.15em] text-[#ABBDDF] block mb-4 fade-up">
            START WITH A CONVERSATION
          </span>
          
          <h2 className="font-cormorant text-4xl lg:text-5xl text-white mb-6 fade-up">
            Not Sure Where You Fit?
          </h2>
          
          <p className="font-lato-light text-lg leading-[1.85] text-white max-w-[580px] mx-auto mb-10 fade-up">
            Some collaborations begin with clarity. Others begin with curiosity. If you are exploring how your values, resources, or expertise might align with our mission, we invite you to start a conversation. Our team will help identify the most meaningful and responsible pathway forward.
          </p>
          
          <div className="fade-up">
            <a
              href="mailto:hello@entrepreneurladyhustler.com"
              className="inline-block bg-[#9D6E6D] text-white font-josefin text-sm uppercase tracking-[0.1em] px-10 py-5 rounded hover:bg-[#7E8184] transition-colors duration-200 mb-4"
              data-testid="cta-contact-team"
            >
              Contact the LHSC Team →
            </a>
            <p className="font-lato-light text-sm text-[#CFD7DE]">
              hello@entrepreneurladyhustler.com
            </p>
          </div>
        </div>
      </section>
    </>
  );
};

export default GetInvolvedPage;
